import React from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    WORKFLOW_APPROVED,
    WORKFLOW_RERUN,
    YES,
    NO,
} from "const"
import {
    CcReviewComplianceHistory,
    CcReviewReviewHistory,
} from "server/models"
import { StoreType } from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
} from "client/components"
import {
    StatisticNotStarted,
    StatisticInProgress,
    StatisticFailed,
    StatisticApproved,
    StatisticValue,
    StatisticStatus,
} from "./styled"


interface StatisticsType {
    all: number
    notStarted: number
    inProgress: number
    failed: number
    approved: number
}

export const ComplianceStatistics = () => {
    const itemList = useSelector((state: StoreType) => state.ccReviewCompliance.itemList)
    const itemHistoryList = useSelector((state: StoreType) => state.ccReviewCompliance.itemHistoryList)
    const version = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)
    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)

    const statistics: StatisticsType = {
        all: 0,
        notStarted: 0,
        inProgress: 0,
        failed: 0,
        approved: 0,
    }

    if (version) {
        Object.values(itemList).forEach((item) => {
            statistics.all += 1

            const itemHistory = (itemHistoryList[item.librarySetId] && itemHistoryList[item.librarySetId][item.id]) || []
            if (itemHistory.length === 0) statistics.notStarted += 1
            else {
                let lastHistory: CcReviewComplianceHistory = null

                // get the last history row made by user, not by 'system'
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage) {
                        lastHistory = history
                        break
                    }
                }

                if (lastHistory && lastHistory.workflow === WORKFLOW_APPROVED) {
                    const response = responseList[lastHistory.responseId] || null
                    if (response && response.approved === YES) statistics.approved += 1
                    if (!response || response.approved === NO) statistics.failed += 1
                } else {
                    statistics.inProgress += 1
                }
            }
        })
    }

    return <Statistics statistics={statistics} />
}

export const ReviewStatistics = () => {
    const itemList = useSelector((state: StoreType) => state.ccReviewReview.itemList)
    const itemHistoryList = useSelector((state: StoreType) => state.ccReviewReview.itemHistoryList)
    const version = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)
    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)

    const statistics: StatisticsType = {
        all: 0,
        notStarted: 0,
        inProgress: 0,
        failed: 0,
        approved: 0,
    }

    if (version) {
        Object.values(itemList).forEach((item) => {
            statistics.all += 1

            const itemHistory = itemHistoryList[item.id] || []
            if (itemHistory.length === 0) statistics.notStarted += 1
            else {
                let lastHistory: CcReviewReviewHistory = null

                // get the last history row made by user, not by 'system'
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage) {
                        lastHistory = history
                        break
                    }
                }

                if (lastHistory && lastHistory.workflow === WORKFLOW_APPROVED) {
                    const response = responseList[lastHistory.responseId] || null
                    if (response && response.approved === YES) statistics.approved += 1
                    if (!response || response.approved === NO) statistics.failed += 1
                } else {
                    statistics.inProgress += 1
                }
            }
        })
    }

    return <Statistics statistics={statistics} />
}


interface DisplayStatisticsProps {
    statistics: StatisticsType
}
const Statistics = ({
    statistics,
}: DisplayStatisticsProps) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const notStarted = statistics.all === statistics.notStarted
    const inProgress = statistics.inProgress > 0
    const failed = statistics.all === (statistics.failed + statistics.approved) && statistics.failed > 0
    const approved = statistics.all === statistics.approved

    const getPercent = (value: number) => {
        const percents = (value / statistics.all) * 100
        return Math.round(percents * 100) / 100 // round to 0.01
    }

    if (!statistics.all) return null
    return (
        <PageForm noPaddingTop>
            <Row>
                <Col xs={4}>
                    <FormGroup>
                        <Label>{lang.reviewStatus}</Label>
                        <StatisticStatus
                            notStarted={notStarted}
                            inProgress={inProgress}
                            failed={failed}
                            approved={approved}
                        >
                            {notStarted && lang.notStarted}
                            {inProgress && lang.inProgress}
                            {failed && lang.failed}
                            {approved && lang.approved}
                        </StatisticStatus>
                    </FormGroup>
                </Col>
                <Col xs={8}>
                    <FormGroup>
                        <Label>{lang.reviewStatistics}</Label>
                        <Row>
                            <Col xs={12}>
                                <StatisticNotStarted>
                                    {`${lang.notStarted}: `}
                                    <StatisticValue>
                                        {`${statistics.notStarted} (${getPercent(statistics.notStarted)}%)`}
                                    </StatisticValue>
                                </StatisticNotStarted>

                                <StatisticInProgress>
                                    {`${lang.inProgress}: `}
                                    <StatisticValue>
                                        {`${statistics.inProgress} (${getPercent(statistics.inProgress)}%)`}
                                    </StatisticValue>
                                </StatisticInProgress>

                                <StatisticFailed>
                                    {`${lang.failed}: `}
                                    <StatisticValue>
                                        {`${statistics.failed} (${getPercent(statistics.failed)}%)`}
                                    </StatisticValue>
                                </StatisticFailed>

                                <StatisticApproved>
                                    {`${lang.approved}: `}
                                    <StatisticValue>
                                        {`${statistics.approved} (${getPercent(statistics.approved)}%)`}
                                    </StatisticValue>
                                </StatisticApproved>
                            </Col>
                        </Row>
                    </FormGroup>
                </Col>
            </Row>
        </PageForm>
    )
}
